import React, { useState, useEffect, useRef } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyD3zBvJb98uxd3Bt1EFIpgSFv2Rta_mASk",
  authDomain: "fence-estimator-saas.firebaseapp.com",
  projectId: "fence-estimator-saas",
  storageBucket: "fence-estimator-saas.firebasestorage.app",
  messagingSenderId: "228487635583",
  appId: "1:228487635583:web:479641b13e1eb6a7bd554a"
};

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
const db = getFirestore(app);

const ZIP_MULTIPLIERS = {
  '100': 1.15, '101': 1.15, '102': 1.12, '103': 1.10, '104': 1.10,
  '105': 1.08, '106': 1.08, '107': 1.05, '108': 1.05, '109': 1.05,
  '200': 1.05, '201': 1.12, '202': 1.15, '203': 1.10, '204': 1.08,
  '205': 1.08, '206': 1.10, '207': 1.12, '208': 1.08, '209': 1.05,
  '210': 1.05, '211': 1.08, '212': 1.10, '213': 1.08, '214': 1.05,
  '215': 1.08, '216': 1.10, '217': 1.08, '218': 1.05, '219': 1.05,
  '220': 1.02, '221': 1.00, '222': 1.02, '223': 1.05, '224': 1.05,
  '225': 1.02, '226': 1.00, '227': 1.02, '228': 1.00, '229': 1.00,
  '300': 1.05, '301': 1.12, '302': 1.10, '303': 1.15, '304': 1.12,
  '305': 1.15, '306': 1.12, '307': 1.08, '308': 1.05, '309': 1.05,
  '310': 1.20, '311': 1.18, '312': 1.15, '313': 1.12, '314': 1.10,
  '315': 1.08, '316': 1.12, '317': 1.10, '318': 1.05, '319': 1.08,
  '320': 1.08, '321': 1.15, '322': 1.12, '323': 1.10, '324': 1.15,
  '325': 1.08, '326': 1.10, '327': 1.12, '328': 1.15, '329': 1.12,
  '330': 1.05, '331': 1.08, '332': 1.10, '333': 1.12, '334': 1.10,
  '335': 1.08, '336': 1.05, '337': 1.08, '338': 1.10, '339': 1.12
};

function CustomerEstimator() {
  const companyId = window.location.pathname.split('/estimate/')[1];

  const [fences, setFences] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedFence, setSelectedFence] = useState(null);
  const [propertyImageUrl, setPropertyImageUrl] = useState(null);
  const [fenceHeight, setFenceHeight] = useState(6);
  const [linearFeet, setLinearFeet] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [totalPrice, setTotalPrice] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);

  const canvasRef = useRef(null);

  useEffect(() => {
    if (companyId) {
      loadCompanyData(companyId);
    } else {
      setError('Invalid company link');
      setLoading(false);
    }
  }, [companyId]);

  const loadCompanyData = async (userId) => {
    try {
      const fencesQuery = query(collection(db, 'fences'), where('userId', '==', userId));
      const fencesSnapshot = await getDocs(fencesQuery);
      const loadedFences = [];
      fencesSnapshot.forEach((doc) => {
        loadedFences.push({ id: doc.id, ...doc.data() });
      });
      setFences(loadedFences);

      const settingsQuery = query(collection(db, 'companySettings'), where('userId', '==', userId));
      const settingsSnapshot = await getDocs(settingsQuery);
      if (!settingsSnapshot.empty) {
        const settings = settingsSnapshot.docs[0].data();
        setCompanySettings(settings);
        setCompanyName(settings.companyName || 'Your Fence Company');
      } else {
        setCompanySettings({
          pricingSettings: {
            laborPercentage: 65,
            profitMargin: 15,
            materialMarkup: 0
          }
        });
        setCompanyName('Your Fence Company');
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading company data:', err);
      setError('Error loading company data. Please try again.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propertyImageUrl && selectedFence && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      const propertyImg = new Image();
      propertyImg.crossOrigin = 'anonymous';
      propertyImg.src = propertyImageUrl;
      
      propertyImg.onload = () => {
        canvas.width = propertyImg.width;
        canvas.height = propertyImg.height;
        ctx.drawImage(propertyImg, 0, 0);
        
        if (selectedFence.imageUrl) {
          const fenceImg = new Image();
          fenceImg.crossOrigin = 'anonymous';
          fenceImg.src = selectedFence.imageUrl;
          
          fenceImg.onload = () => {
            const fenceHeightPx = canvas.height * 0.4;
            const fenceAspectRatio = fenceImg.width / fenceImg.height;
            const fenceWidthPx = fenceHeightPx * fenceAspectRatio;
            const fenceY = canvas.height * 0.55;
            const numTiles = Math.ceil(canvas.width / fenceWidthPx) + 1;
            
            ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
            ctx.shadowBlur = 8;
            ctx.shadowOffsetY = 4;
            
            for (let i = 0; i < numTiles; i++) {
              ctx.drawImage(fenceImg, i * fenceWidthPx - 2, fenceY, fenceWidthPx + 4, fenceHeightPx);
            }
            
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.shadowOffsetY = 0;
          };
          
          fenceImg.onerror = () => {
            console.error('Failed to load fence image');
          };
        }
      };
      
      propertyImg.onerror = () => {
        console.error('Failed to load property image');
      };
    }
  }, [propertyImageUrl, selectedFence]);

  const handlePropertyImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (event) => setPropertyImageUrl(event.target.result);
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const calculateEstimate = () => {
    if (!selectedFence || !linearFeet || !zipCode) {
      alert('Please select a fence style, enter linear feet, and ZIP code');
      return;
    }

    const feet = parseFloat(linearFeet);
    const zip3 = zipCode.substring(0, 3);
    const zipMultiplier = ZIP_MULTIPLIERS[zip3] || 1.0;
    
    const heightMultipliers = { 3: 0.7, 4: 0.85, 5: 0.95, 6: 1.0, 7: 1.15, 8: 1.3 };
    const heightMult = heightMultipliers[fenceHeight] || 1.0;

    // FIX: Properly extract pricingSettings from nested structure
    const pricingSettings = companySettings?.pricingSettings || {
      laborPercentage: 65,
      profitMargin: 15,
      materialMarkup: 0
    };

    const baseMaterialCost = selectedFence.basePrice * feet * heightMult * zipMultiplier;
    const materialCost = baseMaterialCost * (1 + pricingSettings.materialMarkup / 100);
    const laborCost = materialCost * (pricingSettings.laborPercentage / 100);
    const subtotal = materialCost + laborCost;
    const profitAmount = subtotal * (pricingSettings.profitMargin / 100);
    const total = subtotal + profitAmount;

    setTotalPrice({
      total: total.toFixed(2),
      perFoot: (total / feet).toFixed(2),
      feet: feet,
      fenceName: selectedFence.name,
      height: fenceHeight
    });

    setShowContactForm(true);
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setQuoteSent(true);
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingBox}>
          <h2>🏗️ Loading Fence Estimator...</h2>
          <p>Please wait while we load your options.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingBox}>
          <h2>❌ Error</h2>
          <p>{error}</p>
          <p>Please check your link and try again.</p>
        </div>
      </div>
    );
  }

  if (quoteSent) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1>🏗️ {companyName}</h1>
          <p>Professional Fence Estimator</p>
        </div>
        <div style={styles.successBox}>
          <h2>✅ Thank You, {customerName}!</h2>
          <p style={{fontSize: '18px', margin: '20px 0'}}>Your fence quote request has been received.</p>
          <div style={styles.quoteBox}>
            <h3>Your Estimate Summary:</h3>
            <p><strong>Fence Style:</strong> {totalPrice?.fenceName}</p>
            <p><strong>Height:</strong> {totalPrice?.height} ft</p>
            <p><strong>Length:</strong> {totalPrice?.feet} linear feet</p>
            <p style={{fontSize: '24px', color: '#667eea', fontWeight: 'bold', marginTop: '15px'}}>
              Estimated Total: ${totalPrice?.total}
            </p>
            <p style={{color: '#666'}}>Per Linear Foot: ${totalPrice?.perFoot}</p>
          </div>
          <p style={{marginTop: '20px', color: '#666'}}>
            A representative will contact you at {customerEmail || customerPhone} to confirm your quote and schedule a consultation.
          </p>
          <p style={{fontSize: '14px', color: '#999', marginTop: '10px'}}>
            * This is an estimate only. Final pricing may vary based on site conditions and specific requirements.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>🏗️ {companyName}</h1>
        <p>Professional Fence Estimator - Get Your Instant Quote!</p>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.stepBadge}>1</span>
          Choose Your Fence Style
        </h2>
        
        {fences.length === 0 ? (
          <p style={{color: '#666', textAlign: 'center', padding: '20px'}}>
            No fence styles available. Please contact the company directly.
          </p>
        ) : (
          <div style={styles.fenceGrid}>
            {fences.map((fence) => (
              <div
                key={fence.id}
                style={{
                  ...styles.fenceCard,
                  ...(selectedFence?.id === fence.id ? styles.fenceCardSelected : {})
                }}
                onClick={() => setSelectedFence(fence)}
              >
                <img src={fence.imageUrl} alt={fence.name} style={styles.fenceImage} />
                <h3 style={styles.fenceName}>{fence.name}</h3>
                <p style={styles.fenceDetail}>{fence.type} - {fence.style}</p>
                <p style={styles.fenceDetail}>Color: {fence.color}</p>
                {selectedFence?.id === fence.id && (
                  <div style={styles.selectedBadge}>✓ Selected</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.stepBadge}>2</span>
          Preview on Your Property (Optional)
        </h2>
        
        <label style={styles.uploadButton}>
          <input
            type="file"
            accept="image/*"
            onChange={handlePropertyImageChange}
            style={{display: 'none'}}
          />
          📸 Upload a Photo of Your Property
        </label>

        {propertyImageUrl && (
          <div style={{marginTop: '20px', textAlign: 'center'}}>
            <canvas 
              ref={canvasRef} 
              style={{maxWidth: '100%', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.2)'}}
            />
            <p style={{fontSize: '13px', color: '#666', marginTop: '8px'}}>
              * Select a fence style above to see it overlaid on your property
            </p>
          </div>
        )}
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.stepBadge}>3</span>
          Get Your Instant Quote
        </h2>
        
        <div style={styles.calculator}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Fence Height:</label>
            <select
              value={fenceHeight}
              onChange={(e) => setFenceHeight(parseInt(e.target.value))}
              style={styles.input}
            >
              <option value="3">3 ft</option>
              <option value="4">4 ft</option>
              <option value="5">5 ft</option>
              <option value="6">6 ft</option>
              <option value="7">7 ft</option>
              <option value="8">8 ft</option>
            </select>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Approximate Linear Feet:</label>
            <input
              type="number"
              value={linearFeet}
              onChange={(e) => setLinearFeet(e.target.value)}
              placeholder="How many feet of fence do you need?"
              style={styles.input}
            />
            <small style={{color: '#666', fontSize: '12px'}}>
              Tip: Measure the perimeter of the area you want fenced
            </small>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Your ZIP Code:</label>
            <input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              placeholder="Enter your ZIP code"
              maxLength="5"
              style={styles.input}
            />
          </div>

          <button onClick={calculateEstimate} style={styles.calculateButton}>
            🔢 Calculate My Quote
          </button>
        </div>
      </div>

      {totalPrice && showContactForm && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.stepBadge}>4</span>
            Your Fence Quote
          </h2>
          
          <div style={styles.quoteResult}>
            <div style={styles.totalPrice}>
              <p style={{fontSize: '16px', color: '#666', margin: '0'}}>Estimated Total</p>
              <p style={{fontSize: '48px', fontWeight: 'bold', color: '#667eea', margin: '10px 0'}}>
                ${totalPrice.total}
              </p>
              <p style={{fontSize: '16px', color: '#888', margin: '0'}}>
                ${totalPrice.perFoot} per linear foot
              </p>
            </div>
            
            <div style={styles.quoteDetails}>
              <p><strong>Fence Style:</strong> {totalPrice.fenceName}</p>
              <p><strong>Height:</strong> {totalPrice.height} ft</p>
              <p><strong>Length:</strong> {totalPrice.feet} linear feet</p>
            </div>

            <p style={{fontSize: '13px', color: '#999', textAlign: 'center', marginTop: '15px'}}>
              * This is an estimate. Final pricing confirmed after site visit.
            </p>
          </div>

          <div style={styles.contactForm}>
            <h3 style={{color: '#333', marginBottom: '10px'}}>
              📋 Request Your Formal Quote
            </h3>
            <p style={{color: '#666', fontSize: '14px', marginBottom: '20px'}}>
              Enter your details and a representative will contact you to confirm your quote!
            </p>
            
            <form onSubmit={handleContactSubmit}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Your Name:</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter your full name"
                  style={styles.input}
                  required
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label style={styles.label}>Phone Number:</label>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="Enter your phone number"
                  style={styles.input}
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email Address:</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="Enter your email address"
                  style={styles.input}
                />
              </div>

              <button type="submit" style={styles.submitButton}>
                ✅ Send Me My Quote!
              </button>
            </form>
          </div>
        </div>
      )}

      <div style={styles.footer}>
        <p>Powered by FenceQuote Pro</p>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#f5f7ff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  header: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '40px 20px', textAlign: 'center' },
  section: { background: 'white', margin: '20px auto', maxWidth: '900px', borderRadius: '15px', padding: '30px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)' },
  sectionTitle: { color: '#333', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' },
  stepBadge: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: '50%', width: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 'bold', flexShrink: 0 },
  fenceGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' },
  fenceCard: { border: '2px solid #eee', borderRadius: '12px', padding: '15px', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center', position: 'relative' },
  fenceCardSelected: { border: '3px solid #667eea', background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)', boxShadow: '0 4px 15px rgba(102,126,234,0.3)' },
  fenceImage: { width: '100%', height: '130px', objectFit: 'cover', borderRadius: '8px', marginBottom: '10px' },
  fenceName: { fontSize: '14px', fontWeight: 'bold', color: '#333', margin: '5px 0' },
  fenceDetail: { fontSize: '12px', color: '#666', margin: '3px 0' },
  selectedBadge: { background: '#667eea', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', marginTop: '8px', display: 'inline-block' },
  uploadButton: { display: 'inline-block', padding: '12px 25px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '15px' },
  calculator: { maxWidth: '500px', margin: '0 auto' },
  inputGroup: { marginBottom: '20px' },
  label: { display: 'block', color: '#444', fontWeight: '600', marginBottom: '8px', fontSize: '15px' },
  input: { width: '100%', padding: '12px', border: '2px solid #ddd', borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box' },
  calculateButton: { width: '100%', padding: '16px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', marginTop: '10px' },
  quoteResult: { background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)', border: '2px solid #667eea', borderRadius: '12px', padding: '30px', textAlign: 'center', marginBottom: '30px' },
  totalPrice: { marginBottom: '20px' },
  quoteDetails: { display: 'inline-block', textAlign: 'left', background: 'white', padding: '15px 25px', borderRadius: '8px', fontSize: '15px', lineHeight: '2' },
  contactForm: { background: '#f9f9f9', padding: '25px', borderRadius: '12px', border: '1px solid #eee' },
  submitButton: { width: '100%', padding: '16px', background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', marginTop: '10px' },
  footer: { textAlign: 'center', padding: '20px', color: '#999', fontSize: '13px' },
  loadingContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f5f7ff' },
  loadingBox: { background: 'white', padding: '40px', borderRadius: '15px', textAlign: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' },
  successBox: { background: 'white', maxWidth: '600px', margin: '40px auto', padding: '40px', borderRadius: '15px', textAlign: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' },
  quoteBox: { background: '#f0f8ff', border: '2px solid #667eea', borderRadius: '12px', padding: '20px', margin: '20px 0', textAlign: 'left', lineHeight: '2' }
};

export default CustomerEstimator;
